import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const CART_KEY = 'owlfit_cart_v2';

const CartContext = createContext(null);

const loadInitial = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const cartLineId = (item) => `${item._id || item.id || item.slug}::${item.size || 'L'}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadInitial);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {/* storage may be full / blocked */}
  }, [items]);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setIsDrawerOpen((v) => !v), []);

  const addItem = useCallback((product, opts = {}) => {
    const size = opts.size || 'L';
    const quantity = Math.max(1, opts.quantity || 1);
    setItems((prev) => {
      const candidate = { ...product, size };
      const key = cartLineId(candidate);
      const existingIdx = prev.findIndex((it) => cartLineId(it) === key);
      if (existingIdx >= 0) {
        const next = prev.slice();
        next[existingIdx] = {
          ...next[existingIdx],
          quantity: (next[existingIdx].quantity || 1) + quantity,
        };
        return next;
      }
      return [
        ...prev,
        {
          _id: product._id || product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          salePrice: product.salePrice,
          category: product.category,
          type: product.type,
          images: product.images,
          size,
          quantity,
        },
      ];
    });
  }, []);

  const removeItem = useCallback((id, size) => {
    setItems((prev) => prev.filter((it) => !(cartLineId(it) === `${id}::${size || 'L'}`)));
  }, []);

  const updateQuantity = useCallback((id, size, qty) => {
    setItems((prev) =>
      prev
        .map((it) => {
          if (cartLineId(it) !== `${id}::${size || 'L'}`) return it;
          return { ...it, quantity: Math.max(1, qty) };
        })
        .filter((it) => (it.quantity || 1) > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.reduce((s, it) => s + (it.quantity || 1), 0);
    const subtotal = items.reduce(
      (s, it) => s + (it.salePrice ?? it.price) * (it.quantity || 1),
      0
    );
    return {
      items,
      count,
      subtotal,
      addItem,
      removeItem,
      updateQuantity,
      clear,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
    };
  }, [items, addItem, removeItem, updateQuantity, clear, isDrawerOpen, openDrawer, closeDrawer, toggleDrawer]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
